/**
 * 播放类
 */
import { Howl, Howler } from 'howler'
import HlsAudioManager, { primeHlsAudio } from '@/utils/hlsAudioManager'
import locale from '@/locales/index'

// iOS 音频兼容性：设置全局音量并尝试解锁音频上下文
Howler.volume(1.0)
// iOS 需要用户交互后才能正常播放音频，这里预设置
if (typeof window !== 'undefined') {
  // 监听首次用户交互，用于解锁 iOS 音频
  const unlockAudio = () => {
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume()
    }
    document.removeEventListener('touchstart', unlockAudio)
    document.removeEventListener('click', unlockAudio)
  }
  document.addEventListener('touchstart', unlockAudio, { once: true })
  document.addEventListener('click', unlockAudio, { once: true })

  /**
   * HLS 的听力长音频走的是自己 new 出来的 <audio>，完全绕开了上面这套 ——
   * 那段只解锁 Howler 的 AudioContext，对独立的 audio 元素不起作用。
   * 而 iOS 的播放授权是**按元素**的，所以得单独在用户手势里点亮一个备用元素。
   *
   * 不用 { once: true }：预热元素被 HLS 接管后就没了，下一次播放要有新的。
   * 已经备好时 primeHlsAudio 立即返回，重复触发没有开销。
   */
  const primeHls = () => primeHlsAudio()
  document.addEventListener('touchstart', primeHls, true)
  document.addEventListener('click', primeHls, true)
}
// 获取文件url真实地址

function fetchTruthFile(file) {
  file && (file = file.replace(/&{1}t=\w+&{0}|\?t=\w+/g, ''))
  return file
}
// 文件url添加时间戳
function addTimeStamp(file) {
  // 只有以http或者http开头才添加时间戳
  if (/^http/g.test(file)) {
    let truthFile = fetchTruthFile(file)
    if (/\?/g.test(truthFile)) {
      return truthFile + '&t=' + new Date().getTime()
    } else {
      return truthFile + '?t=' + new Date().getTime()
    }
  }

  return file
}
// 判断是否同一文件
function checkSameFile(file1, file2, isTryAgain) {
  if (isTryAgain) {
    return false
  }
  if (file1 === file2) {
    return true
  }
  let truthFile1 = fetchTruthFile(file1)
  let truthFile2 = fetchTruthFile(file2)

  return truthFile1 === truthFile2
}
/**
 * https://github.com/timandy/MediaX/blob/main/doc/Wiki.md
 * 压缩音频
 * @param {*} file
 * @return {*}
 */
const zipAudioFuntion = (file) => {
  if (file.endsWith('.mp3')) {
    return `${file}?format=mp3&bitrate=64k`
  } else {
    return file
  }
}
var Player = function (playlist) {
  this.playlist = playlist || []
  this.rate = 1 //播放速率
  this.lastPlayTime = 0 // 播放剩余时长
  this.currentTimeTracker = null
  this.totalTime = 0 // 播放总的时长，用来中途查看，如果初始化想获取则取duration
  this.isTryAgain = false // 用于音频异常重试

  // HLS 模式相关
  this._hlsManager = null
  this._mode = null // 'hls' | 'howler'
  this._onTimeUpdate = null
  this._onStateChange = null
  this._onError = null
}
Player.prototype = {
  /**
   * Play a song in the playlist.
   * @param  {Number} index Index of the song in the playlist (leave empty to play the first or current).
   */
  play: function (
    file,
    target,
    options = {
      manualStop: false, // 不需要手动停止
    },
    isTryAgain,
    needTimStamp = false,
    isCheckSameFile = true,
    zipAudio = true // 默认开启音频压缩（当前只有海外生效）
  ) {
    var self = this

    // HLS 模式分支
    if (file && String(file).endsWith('.m3u8')) {
      self._mode = 'hls'
      self._hlsMode = true
      self.file = file
      const startTime = options && options.startTime ? options.startTime : 0
      self._playHls(file, startTime)
      return
    }
    // HLS 模式下的最终防线：禁止非 .m3u8 音频切回 Howler 模式
    // allowInHlsMode=true 用于 preview 等必须播放的旧音频
    if (self._hlsMode && !(options && options.allowInHlsMode)) {
      return
    }
    self._mode = 'howler'

    if (needTimStamp) {
      file = addTimeStamp(file)
    }
    if (zipAudio) {
      file = zipAudioFuntion(file)
    }
    self.isTryAgain = isTryAgain
    //支持速率通过本地存储获取，方便hsk全仿真调试
    self.rate = localStorage.getItem('rate') ? Number(localStorage.getItem('rate')) : 1
    var sound
    if (!checkSameFile(self.file, file, isTryAgain)) {
      self.onended = null
      self.onloaded = null
    }
    //如果不在播放列表里，添加进播放列表
    var data = self.playlist.find(function (item) {
      return checkSameFile(item.file, file, isTryAgain)
    })
    if (!data) {
      data = {
        file: file,
        howl: null,
      }
      self.playlist.push(data)
    }
    // 校验网络
    new Howl({
      ...options,
      src: [data.file],
      html5: true,
      // 移动端音频优化：确保音量正常
      volume: 1.0,
      onload: function () {
        sound && sound.duration && self.onloaded && self.onloaded(sound.duration())
      },
      onloaderror: function (e, a) {
        if (a == 4) {
          self.onloaderrored && self.onloaderrored()
          self.setPlayClass()
          self.onended && self.onended('failed')
        }
      },
      onplayerror: function (e, a) {
        analytics.logEvent('hskweb_mockflow_sounderror', { soundurl: data.file })
        self.onplayerrored && self.onplayerrored()
      },
    })
    // sound实例判断
    if (self.sound) {
      self.setPlayClass()
      if (checkSameFile(self.file, file, isTryAgain) && isCheckSameFile) {
        if (self.sound.playing()) {
          self.sound.pause()
        } else {
          self.sound.play()
        }
        return
      } else {
        if ((self.sound.params && !self.sound.params.manualStop) || !isCheckSameFile) {
          self.onUnload && self.onUnload()
          self.sound.unload()
          for (let i = 0; i < self.playlist.length; i++) {
            if (checkSameFile(self.playlist[i].file, file, isTryAgain)) {
              self.playlist[i].howl = null
              break
            }
          }
          self.sound = null
        }
      }
    }
    self.currentData = data
    self.target = target
    // If we already loaded this track, use the current one.
    // Otherwise, setup and load a new Howl.
    if (data.howl) {
      self.sound = sound = data.howl
    } else {
      self.sound =
        sound =
        data.howl =
          new Howl({
            ...options,
            src: [data.file],
            html5: true,
            // 移动端音频优化：确保音量正常
            volume: 1.0,
            rate: self.rate,
            format: ['mp3'],
            onplay: function () {
              self.setPlayClass('play')
              self.currentTimeTracker && clearInterval(self.currentTimeTracker)
              self.currentTimeTracker = setInterval(() => {
                self.lastPlayTime--
              }, 1000)
            },
            onload: function () {
              self.onloaded && self.onloaded(sound.duration())
              self.lastPlayTime = sound.duration()
              self.totalTime = sound.duration()
              console.log('lastPlayTime', self.lastPlayTime)
              console.log('totalTime', self.totalTime)
            },
            onend: function () {
              console.log('onend<<<<<<<<<<<',arguments[0],'<<<<<<<<<<<<<<')
              self.setPlayClass()
              self.onended && self.onended()
              clearInterval(self.currentTimeTracker)
              self.lastPlayTime = 0         
            },
            onplayerror: function () { 
              console.log('play error')
              self.onplayerrored && self.onplayerrored()
            },
            onloaderror: function (e) {
              sound.once('unlock', function () {
                sound.play()
              })
              // self.onended && self.onended('failed')
              self.onloaderrored && self.onloaderrored()
            },
            onpause: function () {
              self.onpaused && self.onpaused()
              self.setPlayClass()
              console.log('onpause<<<<<<<')
            },
            onstop: function () {
              self.setPlayClass()
              console.log('onstop<<<<<<<<')
            },
          })
      self.sound.params = options
    }

    // Begin playing the sound.
    sound.play()
    // Show the pause button.
    if (sound.state() === 'loaded') {
      console.log('loaded 可以播放')
    } else {
      console.log('loading 加载中')
    }
    // Keep track of the index we are currently playing.
    self.file = file
  },

  /**
   * Pause the currently playing track.
   */
  pause: function (file) {
    console.log('pause<<<<<<<<<<<')
    var self = this
    if (!file) {
      file = self.file
    }
    var sound
    // Get the Howl we want to manipulate.
    var data = self.playlist.find(function (item) {
      return checkSameFile(item.file, file, self.isTryAgain)
    })
    if (data) {
      sound = data.howl
    }

    // Puase the sound.
    sound && sound.pause()
    // Show the play button.
  },

  stop: function (file) {
    console.log('stop<<<<<<<<')
    var self = this
    if (self._mode === 'hls' && self._hlsManager) {
      self._hlsManager.stop()
      self._hlsManager.destroy()
      self._hlsManager = null
      self._mode = null
      self.onended = null
      self.file = null
      return
    }
    if (!file) {
      file = self.file
    }
    var sound
    // Get the Howl we want to manipulate.
    var data = self.playlist.find(function (item) {
      return checkSameFile(item.file, file, self.isTryAgain)
    })
    if (data) {
      sound = data.howl
    }
    //移除播放结束回调
    self.onended = null
    self.file = null
    // Puase the sound.
    sound && sound.stop()
    self.rate = 1
  },

  load: function (file) {
    var self = this
    if (!file) {
      file = self.file
    }
    var sound
    // Get the Howl we want to manipulate.
    var data = self.playlist.find(function (item) {
      return checkSameFile(item.file, self.file, self.isTryAgain)
    })
    if (data) {
      sound = data.howl
    }
    //移除播放结束回调
    self.onended = null
    sound && sound.load()
  },

  setRate: function (val) {
    var self = this
    var sound
    var data = self.playlist.find(function (item) {
      return checkSameFile(item.file, self.file, self.isTryAgain)
    })
    if (data) {
      sound = data.howl
    }
    self.rate = val
    sound && sound.rate(val)
  },
  /**
   * Set the volume and update the volume slider display.
   * @param  {Number} val Volume between 0 and 1.
   */
  volume: function (val) {
    var self = this
    // Update the global volume (affecting all Howls).
    Howler.volume(val)
  },

  // ========== HLS 模式方法 ==========
  _playHls: function (url, startTime) {
    var self = this
    if (self._hlsManager) {
      self._hlsManager.destroy()
    }
    self._hlsManager = new HlsAudioManager({
      onTimeUpdate: function (currentTime) {
        self._onTimeUpdate && self._onTimeUpdate(currentTime)
      },
      onStateChange: function (state) {
        self._onStateChange && self._onStateChange(state)
      },
      onError: function (error) {
        self._onError && self._onError(error)
        self.onplayerrored && self.onplayerrored()
      },
      onEnded: function () {
        self.onended && self.onended()
      },
    })
    self._hlsManager.load(url, startTime)
    self._hlsManager.play()
  },

  getCurrentTime: function () {
    if (this._mode === 'hls' && this._hlsManager) {
      return this._hlsManager.currentTime
    }
    return 0
  },

  getDuration: function () {
    if (this._mode === 'hls' && this._hlsManager) {
      return this._hlsManager.duration
    }
    return 0
  },

  getState: function () {
    if (this._mode === 'hls' && this._hlsManager) {
      return this._hlsManager.state
    }
    return null
  },

  seek: function (time) {
    if (this._mode === 'hls' && this._hlsManager) {
      this._hlsManager.seek(time)
    }
  },

  resume: function () {
    if (this._mode === 'hls' && this._hlsManager) {
      this._hlsManager.resume()
    }
  },

  onTimeUpdate: function (cb) {
    this._onTimeUpdate = cb
  },

  onStateChange: function (cb) {
    this._onStateChange = cb
  },

  onError: function (cb) {
    this._onError = cb
  },

  // ========== HLS 模式方法结束 ==========

  setPlayClass: function (type) {
    var self = this
    if (self.target) {
      let target = self.target.currentTarget || self.target.target || self.target
      let domChildren = target.children
      if (type == 'play') {
        target.classList.add('ing')
        if (domChildren.length) {
          domChildren[0].classList.add('ing')
        }
      } else {
        target.classList.remove('ing')
        if (domChildren.length) {
          domChildren[0].classList.remove('ing')
        }
      }
    }
  }
}
Player.prototype.onended = null

export default Player
